import { useMutation, useQuery } from "@apollo/client";
import { Alert, Autocomplete, TextField } from "@mui/material";
import { Dialog } from "components/Dialog";
import {
  FacilityAccumulationAreaOptionsQuery,
  WasteAccumulationAreaType,
} from "generated-graphql/graphql";
import { useState } from "react";

import { FACILITY_ACCUMULATION_AREA_OPTIONS_QUERY, MOVE_CONTAINER_MUTATION } from "./api";

type AreaOption = NonNullable<
  NonNullable<FacilityAccumulationAreaOptionsQuery["facility"]>["accumulationAreas"]
>["items"][number];

export type MoveContainerDialogContainer = {
  id: string;
  facilityId: string;
  isPermanent: boolean;
  accumulationArea?: { id: string; name: string; type: WasteAccumulationAreaType } | null;
};

export type MoveContainerDialogProps = {
  container: MoveContainerDialogContainer;
  open: boolean;
  onClose: () => void;
};

function areaTypeLabel(type: WasteAccumulationAreaType): string {
  return type === WasteAccumulationAreaType.Central ? "Central" : "Satellite";
}

/**
 * Confirming a move to another area. Target options are pre-filtered
 * client-side to the same placement rules the create form enforces: a
 * permanent container (tank), or one already in central accumulation, can
 * only move to another central area — the mutation would reject anything
 * else, but surfacing that as an unreachable option is friendlier than a
 * round-trip error.
 */
export function MoveContainerDialog({ container, open, onClose }: MoveContainerDialogProps) {
  const [selectedArea, setSelectedArea] = useState<AreaOption | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data } = useQuery(FACILITY_ACCUMULATION_AREA_OPTIONS_QUERY, {
    variables: { facilityId: container.facilityId },
    skip: !open,
    // Revalidate on open: a newly created area may not be in the cached list
    // (area-create's refetch only hits this query while it's mounted, and it's
    // skipped until the dialog opens), so cache-first would show a stale dropdown.
    fetchPolicy: "cache-and-network",
  });
  const areaOptions: AreaOption[] = data?.facility?.accumulationAreas?.items ?? [];

  const centralOnly =
    container.isPermanent || container.accumulationArea?.type === WasteAccumulationAreaType.Central;

  const targetOptions = areaOptions.filter(
    (area) =>
      area.id !== container.accumulationArea?.id &&
      (!centralOnly || area.type === WasteAccumulationAreaType.Central)
  );

  const [moveContainer, { loading }] = useMutation(MOVE_CONTAINER_MUTATION, {
    // A move shifts volume between two areas, so a mounted detail page or the
    // areas list must refresh their gauges alongside the container lists.
    refetchQueries: [
      "GetWasteContainer",
      "TenantWasteContainers",
      "FacilityWasteContainers",
      "GetAccumulationArea",
      "FacilityAccumulationAreas",
      "FacilityAccumulationSummary",
    ],
  });

  const handleClose = () => {
    setSelectedArea(null);
    setErrorMessage(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!selectedArea) {
      return;
    }
    setErrorMessage(null);
    await moveContainer({
      variables: {
        input: { containerId: container.id, toAccumulationAreaId: selectedArea.id },
      },
      onCompleted: () => {
        handleClose();
      },
      onError: (error) => {
        setErrorMessage(error.message || "An error occurred while moving the container");
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      title="Move container"
      primaryActionButtonProps={{
        loading,
        onClick: handleConfirm,
        disabled: !selectedArea,
        saveText: "Move",
      }}
    >
      <Autocomplete
        options={targetOptions}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={selectedArea}
        onChange={(_event, value) => setSelectedArea(value)}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {option.name} ({areaTypeLabel(option.type)})
          </li>
        )}
        renderInput={(params) => <TextField {...params} label="Target area" autoFocus />}
      />
      {selectedArea?.type === WasteAccumulationAreaType.Central && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Moving to central accumulation starts the central time clock.
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}
    </Dialog>
  );
}
